import { FC, MouseEvent, useState } from 'react'
import useHover from 'hooks/useHover'
import formatTime from 'helpers/time/formatTime'
import useLazyMedia from 'hooks/useLazyMedia'
import { cn } from '@wellesley/ui'
import useTimeline from './hooks/useTimeline'
import useVideoControl from './hooks/useVideoControls'
import VideoPanel from './VideoPanel'
import {
  isBlobSource,
  isExtremeCase,
  isPlayableVideo,
  selectUploadFile,
} from '@wellesley/uploads'
import { SMALL_PREVIEW_SIZE } from 'constants/ui'
import { platformSettingsSlice } from '@wellesley/settings'
import { useSelector } from 'react-redux'
import useVideoEnd from './hooks/useVideoEnd'
import PreviewLabel from '../PreviewLabel'
import PlayToggler from './PlayToggler'
import { X } from 'lucide-react'
import { VideoProps } from './VideoPanel/types'
import useHLSVideo from 'hooks/useHLSVideo'
import { useVideoContext } from './ActiveVideoContext'

export const EXTREME_VIDEO_BREAKPOINT = 120

const Video: FC<VideoProps> = ({
  sizes,
  upload,
  className,
  mediaHeight,
  inView = true,
  containerWidth = 568,
  canPlayInline = false,
  canPlayInModal = false,
  isSmallPreview = false,
  onClosePreview,
}) => {
  const videoExtensions = useSelector(
    platformSettingsSlice.selectors.selectVideoExtensions,
  )

  const previewUploadFile = selectUploadFile({
    upload,
    allowedExtensions: videoExtensions,
    size: SMALL_PREVIEW_SIZE,
  })

  const bestFitUploadFile = selectUploadFile({
    upload,
    allowedExtensions: videoExtensions,
    size: containerWidth,
  })

  const thumbnail = selectUploadFile({
    upload: {
      ...upload,
      uploadType: 'IMAGE',
    },
    size: containerWidth,
  })

  const uploadFile = isSmallPreview ? previewUploadFile : bestFitUploadFile

  const ratio = (uploadFile?.meta.width || 1) / (uploadFile?.meta.height || 1)

  const [hoverRef, isHovered] = useHover<HTMLDivElement>()

  const uploadSrc = uploadFile?.uri
  const isLocalVideo = isBlobSource(uploadSrc || '')

  const { onLoad, isLoaded, wrapperRef, placeholder } = useLazyMedia({
    ratio,
    isLocalFile: isLocalVideo,
    blurHash: upload.meta?.blurhash,
  })

  const {
    muted,
    volume,
    videoRef,
    isPlaying,
    togglePlay,
    toggleMute,
    pauseVideo,
    setIsPlaying,
    handleVolumeChange,
  } = useVideoControl({
    id: upload.uploadId,
    inView,
    wrapperRef,
    canPlayInline: canPlayInline && !isLocalVideo,
    canPlayInModal,
  })

  const duration = uploadFile?.meta.duration || 0

  const { handleTimeUpdate, ...videoPanelProps } = useTimeline({
    videoRef,
    duration,
    canPlayInModal,
  })

  const canPlayVideo =
    uploadFile &&
    isPlayableVideo({ file: uploadFile, allowedExtensions: videoExtensions })

  const [isVideoLoaded, setVideoLoaded] = useState(
    isLocalVideo || !thumbnail?.uri,
  )

  const { updateCurrentTime } = useVideoContext()

  const [isEnded, resetEnded, handleVideoEnd] = useVideoEnd({
    onVideoEnd: () => {
      setIsPlaying(false)
      updateCurrentTime(0)
    },
  })

  const isExtremeVideo = isExtremeCase(bestFitUploadFile)

  const canShowPlayIcon = isLoaded && !isPlaying && !isSmallPreview
  const canShowPauseIcon = isLoaded && isPlaying && isHovered
  const canShowRestartIcon = isEnded && !isSmallPreview
  const canShowPlayToggle =
    (canShowPlayIcon || canShowPauseIcon || canShowRestartIcon) &&
    !isExtremeVideo &&
    canPlayVideo
  const canShowThumbnail = thumbnail?.uri && !isVideoLoaded
  const canShowVideoPanel = isHovered && !isExtremeVideo
  const canShowDuration = !!(
    !canShowVideoPanel &&
    videoPanelProps.currentTime &&
    canPlayVideo
  )

  const handleVideoClick = () => {
    canPlayInModal ? togglePlay() : isPlaying && pauseVideo()
  }

  const handleTogglePlay = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()

    togglePlay()
  }

  const handleVideoPlaying = () => {
    setIsPlaying(true)
    setVideoLoaded(true)
    isEnded && resetEnded()
  }

  const closeFullscreen = () => {
    if (document.fullscreenElement) {
      void document.exitFullscreen()
    }
  }

  const onFullscreen = async () => {
    try {
      if (wrapperRef.current) {
        if (document.fullscreenElement) {
          closeFullscreen()
        } else {
          await wrapperRef.current.requestFullscreen()
        }
      }
    } catch (e) {
      console.error('Error toggling fullscreen mode:', e)
    }
  }

  const isFullscreen = document.fullscreenElement === wrapperRef.current

  useHLSVideo({ videoRef, videoSrc: uploadSrc || '', onInit: onLoad })

  if (
    !uploadFile ||
    !uploadSrc ||
    !uploadFile.meta.width ||
    !uploadFile.meta.height
  )
    return null

  return (
    <div
      style={{
        height: isExtremeVideo
          ? `${EXTREME_VIDEO_BREAKPOINT}px`
          : isSmallPreview
            ? undefined
            : mediaHeight && `${mediaHeight}px`,
      }}
      className={cn('relative h-full', !canPlayInModal && 'rounded-md')}
      ref={hoverRef}
      onClick={handleVideoClick}
    >
      <div
        ref={wrapperRef}
        className="flex h-full flex-col justify-center"
        data-testid="video-wrapper"
      >
        {isFullscreen && (
          <X
            data-testid="close-fullscreen"
            className="absolute right-[52px] top-[52px] z-10 cursor-pointer text-white"
            onClick={closeFullscreen}
          />
        )}

        {canShowThumbnail && (
          <>
            {placeholder}
            <img
              src={thumbnail.uri!}
              alt={upload.meta?.name || ''}
              onLoad={onLoad}
              className={cn(className, 'h-full', { hidden: !isLoaded })}
            />
          </>
        )}

        <video
          muted={muted}
          src={uploadSrc}
          ref={videoRef}
          preload={thumbnail?.uri ? 'none' : 'metadata'}
          onEnded={handleVideoEnd}
          onPlaying={handleVideoPlaying}
          onTimeUpdate={handleTimeUpdate}
          className={cn(className, 'h-full', {
            'absolute left-0 top-0 opacity-0': !isVideoLoaded,
          })}
          playsInline
          controls={false}
          data-testid="video"
          style={
            isLocalVideo
              ? { width: sizes?.width, height: sizes?.height, opacity: 1 }
              : undefined
          }
        >
          <source src={uploadSrc} />
        </video>
        {canShowPlayToggle && (
          <PlayToggler
            position="center"
            togglePlay={handleTogglePlay}
            {...{ isEnded, isHovered, isPlaying }}
          />
        )}

        {canShowVideoPanel && (
          <VideoPanel
            videoDuration={duration}
            {...videoPanelProps}
            {...{
              muted,
              volume,
              isPlaying,
              togglePlay,
              toggleMute,
              isSmallPreview,
            }}
            isBigPreview={canPlayInModal}
            containerRef={hoverRef}
            onVolumeChange={handleVolumeChange}
            onClosePreview={onClosePreview}
            onFullscreen={() => void onFullscreen()}
          />
        )}

        {canShowDuration && (
          <PreviewLabel
            position="bottom-left"
            className="px-xxs py-xxs min-w-11 rounded-md"
          >
            {formatTime(videoPanelProps.currentTime)}
          </PreviewLabel>
        )}
      </div>
    </div>
  )
}

export { Video }
